import type { ExtraBags } from "@b2bportal/air-baggage-api";
import {
  BaggageInfoEnum,
  type ItineraryBaggage,
  type ExtraBaggageOptions,
  OrderedExtraBagsEnum,
} from "@b2bportal/air-booking-api";
import type { FiatPrice } from "@b2bportal/air-shopping-api";
import { Product } from "@b2bportal/purchase-api";
import type { FlightContext } from "@checkout/states";
import { ParentState } from "@checkout/types";
import type { State } from "xstate";
import { getFlightSelectedTrackingProperties } from "../../selectors";

type BaggageStateType = State<FlightContext>;
type BaggageStateWithoutValue = Pick<BaggageStateType, "context">;

export const getExtraBaggage = (
  state: BaggageStateWithoutValue
): ExtraBaggageOptions | undefined => {
  const flightProduct = state.context[
    ParentState.cartQuote
  ].quoteBreakdown?.products.find((p) => p.product.type === Product.Flight);
  return flightProduct?.product?.value?.pricingSummary?.extraBaggage;
};

export const getCarryOnAllowance = (
  state: BaggageStateWithoutValue
): number => {
  const extraBaggage = getExtraBaggage(state);
  if (extraBaggage == null || extraBaggage.carryOnBag == null) {
    return 0;
  }
  switch (extraBaggage?.carryOnBag?.BaggageInfo) {
    case BaggageInfoEnum.Allowed:
      return extraBaggage.carryOnBag.allowance.pieces;
    case BaggageInfoEnum.NotAllowed:
    case BaggageInfoEnum.Unknown:
      return 0;
  }
};

export const getCheckedBaggageAllowance = (
  state: BaggageStateWithoutValue
): number => {
  const extraBaggage = getExtraBaggage(state);
  if (extraBaggage == null || extraBaggage.carryOnBag == null) {
    return 0;
  }
  switch (extraBaggage?.checkedBag?.BaggageInfo) {
    case BaggageInfoEnum.Allowed:
      return extraBaggage.checkedBag.allowance.pieces;
    case BaggageInfoEnum.NotAllowed:
    case BaggageInfoEnum.Unknown:
      return 0;
  }
};

export const getPersonalItemAllowance = (
  state: BaggageStateWithoutValue
): number => {
  const extraBaggage = getExtraBaggage(state);
  if (extraBaggage == null || extraBaggage.personalItem == null) {
    return 0;
  }
  switch (extraBaggage?.personalItem?.BaggageInfo) {
    case BaggageInfoEnum.Allowed:
      return extraBaggage.personalItem.allowance.pieces;
    case BaggageInfoEnum.NotAllowed:
    case BaggageInfoEnum.Unknown:
      return 0;
  }
};

export const getCheckedBagsPrices = (
  state: BaggageStateWithoutValue
): Array<FiatPrice> => {
  return state.context[ParentState.bagsSelection].checkedBagsPrices;
};

export const getPassengerExtraBaggageMap = (
  state: BaggageStateWithoutValue
): Map<string, ExtraBags> => {
  return state.context[ParentState.bagsSelection].passengerBagsMap;
};

export const getPassengerBagPriceMap = (
  state: BaggageStateWithoutValue
): Map<string, Omit<FiatPrice, "currencySymbol">> => {
  const passengerBagPriceMap: Map<
    string,
    Omit<FiatPrice, "currencySymbol">
  > = new Map();
  const checkedBagsPrices: Array<FiatPrice> =
    state.context[ParentState.bagsSelection].checkedBagsPrices;
  const passengerBagsMap =
    state.context[ParentState.bagsSelection].passengerBagsMap;
  for (const [passengerId, bags] of passengerBagsMap) {
    let totalPrice = 0;
    let currencyCode =
      checkedBagsPrices.length > 0 ? checkedBagsPrices[0].currencyCode : "";
    for (let i = 0; i < bags.checkedBags; i++) {
      if (!currencyCode) {
        currencyCode = checkedBagsPrices[i].currencyCode;
      }
      if (checkedBagsPrices[i].currencyCode !== currencyCode) {
        console.error("Currency code not consistent");
      }
      totalPrice += checkedBagsPrices[i]?.value;
    }
    passengerBagPriceMap.set(passengerId, {
      currencyCode: currencyCode,
      value: totalPrice,
    });
  }
  return passengerBagPriceMap;
};

export const hasExtraBaggageAdded = (
  state: BaggageStateWithoutValue
): boolean => {
  const passengerBagsMap: Map<string, ExtraBags> =
    state.context[ParentState.bagsSelection].passengerBagsMap;
  if (passengerBagsMap == null) {
    return false;
  }
  for (const bags of passengerBagsMap.values()) {
    // We consider baggage selected even users choose 0 piece of baggage
    if (bags.checkedBags >= 0) return true;
  }
  return false;
};

export const extraBaggageCountTotal = (
  state: BaggageStateWithoutValue
): number => {
  const passengerBagsMap =
    state.context[ParentState.bagsSelection].passengerBagsMap;
  let total = 0;
  if (passengerBagsMap === null) {
    return total;
  }
  for (const bags of passengerBagsMap.values()) {
    total += bags.checkedBags;
  }
  return total;
};

export const passengersWithAddedBagsCount = (
  state: BaggageStateWithoutValue
): number => {
  const passengerBagsMap =
    state.context[ParentState.bagsSelection].passengerBagsMap;
  return passengerBagsMap === null ? 0 : Object.keys(passengerBagsMap).length;
};

export const extraBaggageCostTotalCombined = (
  state: BaggageStateWithoutValue
) => {
  const passengerBagPriceMap = getPassengerBagPriceMap(state);
  const checkedBagsPrices = getCheckedBagsPrices(state);
  if (checkedBagsPrices == null || checkedBagsPrices.length === 0) {
    return {
      total: 0,
      currencyCode: "USD",
    };
  }
  // add up the values, we can assume same currency code as there's a check above
  let total = 0;
  for (const price of passengerBagPriceMap.values()) {
    total += price.value;
  }
  return {
    total: total,
    currencyCode: checkedBagsPrices[0]?.currencyCode,
  };
};

export const areBagsInCartUpdate = ({ context }: BaggageStateWithoutValue) =>
  context[ParentState.cartUpdate].addProducts.findIndex(
    (product) => product.type === Product.AirBaggage
  ) >= 0;

export const getPurchasedBagCount = (
  state: BaggageStateWithoutValue
): number => {
  const products = state.context[ParentState.cartFulfill].fulfilledProducts;
  const baggageProduct: ItineraryBaggage | undefined = products?.find(
    (p) => p.type === Product.AirBaggage
  )?.value;
  if (baggageProduct == null) return 0;
  return getPurchasedBagCountFromItinerary(baggageProduct);
};

export const getPurchasedBagCountFromItinerary = (
  baggageProduct: ItineraryBaggage
): number => {
  switch (baggageProduct.orderedExtraBags.OrderedExtraBags) {
    case OrderedExtraBagsEnum.AllPassengerExtraBags: {
      const {
        carryOnBags = [],
        checkedBags = [],
        personalItems = [],
      } = baggageProduct.orderedExtraBags.bags;
      return carryOnBags.length + checkedBags.length + personalItems.length;
    }
    case OrderedExtraBagsEnum.PerPassengerExtraBags:
      return Object.values(
        baggageProduct.orderedExtraBags.bagsPerPassenger
      ).reduce((sum, passenger) => {
        const {
          carryOnBags = [],
          checkedBags = [],
          personalItems = [],
        } = passenger;
        return (
          sum + carryOnBags.length + checkedBags.length + personalItems.length
        );
      }, 0);
  }
};

export const baseBagsTrackingProperties = (context: FlightContext) => {
  // We can check breakdown since these fire only after quote
  const baseFlightSelectedTrackingProperties =
    getFlightSelectedTrackingProperties({ context });
  const properties = {
    // this event only fires when bags are available, handled by the state machine
    extra_bags_selection_available: true,
    extra_bags_personal_item_allowance: getPersonalItemAllowance({ context }),
    extra_bags_carry_on_allowance: getCarryOnAllowance({ context }),
    // extra_bags_carry_on_allowance_weight: null,
    extra_bags_checked_bag_allowance: getCheckedBaggageAllowance({ context }),
    // extra_bags_checked_bag_allowance_weight: null,
    extra_bags_carry_on_unit_price:
      context[ParentState.bagsSelection].checkedBagsPrices[0]?.value,
    extra_bags_carry_on_unit_price_currency:
      context[ParentState.bagsSelection].checkedBagsPrices[0]?.currencyCode,
    // extra_bags_carry_on_unit_price_usd: null,
    // extra_bags_carry_on_per_passenger: null,
    // extra_bags_checked_bag_unit_price_usd: null,
    ...baseFlightSelectedTrackingProperties,
  };
  return properties;
};

export const confirmedExtaBagsTrackingProperties = (context: FlightContext) => {
  const totalCostCombined = extraBaggageCostTotalCombined({ context });
  const properties = {
    extra_bags_price_total: totalCostCombined.total,
    extra_bags_price_total_currency: totalCostCombined.currencyCode,
    extra_bags_total_travelers_with_checked_bag: passengersWithAddedBagsCount({
      context,
    }),
    extra_bags_total_added_checked_bag: extraBaggageCountTotal({ context }),
    // carry on is not supported (yet)
    extra_bags_total_travelers_with_carry_on: 0,
    extra_bags_total_added_carry_on: 0,
  };
  return properties;
};
